import { useEffect, useState } from "react";
import criteoSpotLightAdvert from "../api/criteoSpotLightApi";
import { useStockPrice } from "../context/stockPrice/StockPriceContext";
import { useRuntimeConstants } from "../context/runtimeConstants/RuntimeConstantsContext";
import isProduction from "../utils/isProduction";
import { logError, ERROR_TYPE } from "../utils/newRelicLogger";

// CriteoSpotLightAdvert renders <img src={mobile_background_image}> as the
// mandatory <picture> child (the desktop background_image is only a <source>
// override). A creative without a mobile image therefore can't paint on mobile
// and would produce an undefined img src, so require mobile_background_image.
const hasRenderableCreative = (item) =>
  Boolean(
    item &&
    typeof item === "object" &&
    item.rendering &&
    item.rendering.mobile_background_image,
  );

// Criteo can return the on-site display (BDSS) banner under different
// placement keys/indices, and alongside other placements (e.g. sponsored
// products) when both are delivered. Scan every placement group rather than
// relying on a fixed key/index so the banner is still found and painted.
const findSpotLightPlacement = (placements = []) => {
  for (const placementGroup of placements) {
    if (!placementGroup || typeof placementGroup !== "object") {
      continue;
    }

    for (const placementValue of Object.values(placementGroup)) {
      if (!Array.isArray(placementValue)) {
        continue;
      }

      const creative = placementValue.find(hasRenderableCreative);

      if (creative) {
        return creative;
      }
    }
  }

  return undefined;
};

const useCriteoSpotLight = () => {
  const [spotLightData, setSpotLightData] = useState(undefined);
  const { products } = useStockPrice();

  const { siteDomain } = useRuntimeConstants();
  const isProd = isProduction(siteDomain);

  useEffect(() => {
    const getSpotlightData = async () => {
      try {
        const spotLightResponse = await criteoSpotLightAdvert(products, isProd);

        const filteredResponse = findSpotLightPlacement(
          spotLightResponse?.placements,
        );

        if (filteredResponse) {
          setSpotLightData(filteredResponse);
        }
      } catch (error) {
        logError(ERROR_TYPE.NonCriticalAppError, {
          message: error?.message ?? String(error),
          action: "CriteoSpotLightApi",
        });
        return undefined;
      }
    };

    const hasRequiredParams = Boolean(products?.id && products?.price);
    if (!spotLightData && hasRequiredParams) {
      getSpotlightData();
    }
  }, [spotLightData, products, isProd]);

  return spotLightData;
};

export default useCriteoSpotLight;
